import { Box, Group, Stack, useMantineTheme } from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import { useSuspenseApiMemberEnv } from '@repo/api-codegen';
import {
	SettingsAvatar,
	SettingsBlock,
	SettingsButton,
	SettingsTextInput,
} from '@repo/common/components';
import { Button, Icon, ResourceIcon, Text } from '@repo/foundations';
import { useDebounceFn } from 'ahooks';
import axios from 'axios';
import { size } from 'lodash-es';
import { observer } from 'mobx-react-lite';
import { useCallback, useState } from 'react';
import {
	queryClient,
	useAuthUser,
	usersQueryKeyFactory,
	useUpdateUser,
	useWorkspace,
} from '../../api';
import { currentUserQueryKey } from '../../api/hooks/user/useCurrentUser';
import { useExtendedUserList } from '../../api/hooks/user/useExtendedUserList';
import { useUserProfileImage } from '../../api/hooks/user/useUserProfileImage';
import { useServiceWorkerUpdate } from '../../hooks/useServiceWorkerUpdate';
import { openModal } from '../ModalManager';
import { UpdateAvailableButton } from '../UpdateAvailableButton';

export const ProfileSettings = observer(() => {
	const { activeUsers } = useExtendedUserList();
	const theme = useMantineTheme();
	const { user } = useAuthUser();
	const { workspace } = useWorkspace();
	const { needRefresh } = useServiceWorkerUpdate();

	const { data } = useSuspenseApiMemberEnv({});

	const versionMismatch = data.TAG !== import.meta.env.VITE_FRONTEND_VERSION;

	// Add state for first and last name
	const [firstName, setFirstName] = useState(user?.first_name || '');
	const [lastName, setLastName] = useState(user?.last_name || '');

	const { mutateAsync: updateProfileImage, isPending: isUpdatingProfileImage } =
		useUserProfileImage();
	const { mutateAsync: updateUser, isPending: isUpdatingUser } = useUpdateUser({
		options: {
			onSuccess: () => {
				queryClient.invalidateQueries({
					queryKey: usersQueryKeyFactory.byId(user.id),
				});
				queryClient.invalidateQueries({
					queryKey: currentUserQueryKey,
				});
			},
		},
	});

	const handleNotification = useCallback((updatedVal: string) => {
		showNotification({
			title: `${updatedVal} updated`,
			message: 'Your profile has been updated.',
			color: 'green',
			autoClose: 1500,
		});
	}, []);

	// Create debounced update functions for first and last name
	const { run: updateFirstName } = useDebounceFn(
		async (value: string) => {
			if (value !== user.first_name) {
				await updateUser({
					data: {
						id: user.id,
						first_name: value,
						last_name: lastName,
					},
				});
				handleNotification('First name');
			}
		},
		{ wait: 1000 }
	);

	const { run: updateLastName } = useDebounceFn(
		async (value: string) => {
			if (value !== user.last_name) {
				await updateUser({
					data: {
						id: user.id,
						first_name: firstName,
						last_name: value,
					},
				});
				handleNotification('Last name');
			}
		},
		{ wait: 1000 }
	);

	const handleDeleteAccountModal = useCallback(() => {
		const handleLeave = async () => {
			await axios.post(`/workspace/${workspace?.id}/leave_workspace/`, {});
			window.location.reload();
		};

		openModal({
			title: 'Are you sure you want to leave this workspace?',
			children: (
				<Button
					data-testid="leave-workspace-modal"
					variant="primary"
					tone="critical"
					onClick={handleLeave}
				>
					Leave workspace
				</Button>
			),
		});
	}, [workspace]);

	const onUpload = useCallback(
		async (file: File | null) => {
			if (file) {
				await updateProfileImage({
					file,
				});
			}
		},
		[updateProfileImage]
	);

	return (
		<Box>
			<Box>
				<SettingsAvatar
					title="Profile picture"
					imageUrl={user?.profile_picture ?? ''}
					onChange={onUpload}
					disabled={isUpdatingProfileImage}
				/>
				<SettingsTextInput
					title="First name"
					value={firstName}
					onChange={(value) => {
						setFirstName(value);
						updateFirstName(value);
					}}
					disabled={isUpdatingUser}
				/>
				<SettingsTextInput
					title="Last name"
					value={lastName}
					onChange={(value) => {
						setLastName(value);
						updateLastName(value);
					}}
					disabled={isUpdatingUser}
				/>
			</Box>
			<Box my={theme.spacing.xl}>
				{needRefresh && !versionMismatch && (
					<SettingsBlock
						title="New app version available"
						leftComponent={<ResourceIcon iconName="rotate" />}
						rightComponent={<UpdateAvailableButton />}
					/>
				)}
				{!needRefresh && !versionMismatch && (
					<SettingsBlock
						title="App is up to date"
						leftComponent={<ResourceIcon iconName="check" />}
						rightComponent={
							<Stack spacing={0}>
								<Group spacing="xs">
									<Icon name="check" size="md" color="icon/primary/active" />
									<Text color="text/secondary/default" size="sm">
										Backend version: {data.TAG}
									</Text>
								</Group>
								<Group spacing="xs">
									<Icon name="check" size="md" color="icon/primary/active" />
									<Text color="text/secondary/default" size="sm">
										Frontend version: {import.meta.env.VITE_FRONTEND_VERSION}
									</Text>
								</Group>
							</Stack>
						}
					/>
				)}
				{!needRefresh && versionMismatch && (
					<SettingsBlock
						leftComponent={<ResourceIcon iconName="rotate" />}
						title="New app version pending"
						rightComponent={
							<Stack spacing={0}>
								<Group spacing="xs">
									<Icon name="check" size="md" color="icon/primary/active" />
									<Text color="text/secondary/default" size="sm">
										Backend version: {data.TAG}
									</Text>
								</Group>
								<Group spacing="xs">
									<Icon
										name="alertCircle"
										size="md"
										color="icon/critical/default"
									/>
									<Text color="text/secondary/default" size="sm">
										Frontend version: {import.meta.env.VITE_FRONTEND_VERSION}
									</Text>
								</Group>
							</Stack>
						}
					/>
				)}
			</Box>
			<Box my={theme.spacing.xl}>
				{size(activeUsers) > 0 && (
					<SettingsButton
						title="Leave workspace"
						buttonText="Leave workspace"
						description="Remove your account from the workspace"
						onClick={handleDeleteAccountModal}
					/>
				)}
			</Box>
		</Box>
	);
});
